import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ONLY_TOOLTIP, Display, Rule, SmallCap, useCanWriteOrg } from '@pc/ui';
import { isAdminRole, useMyOrgRole } from './useMyOrgRole';
import { SettingsSearch } from './SettingsSearch';
import { AnchorWatcher } from './useScrollAnchorHighlight';

// `match` is the URL-prefix the tab should be "active" for. When omitted
// we use the tab's own `to`; the explicit prop is what lets the parent
// "Developer" tab light up while the user is actually on a child route
// like `/settings/developer/webhooks`. Mirror existing React Router
// `NavLink` semantics but with a single prefix, since the dev tab has
// no page of its own (`/settings/developer` redirects into the first
// sub-tab in App.tsx).
const TABS: Array<{
  to: string;
  label: string;
  adminOnly?: boolean;
  match?: string;
}> = [
  { to: '/settings/profile',   label: 'Profile' },
  { to: '/settings/account',   label: 'Account' },
  { to: '/settings/store',     label: 'Store',    adminOnly: true },
  { to: '/settings/security',  label: 'Security' },
  { to: '/settings/messaging', label: 'Messaging', adminOnly: true },
  { to: '/settings/templates', label: 'Templates', adminOnly: true },
  { to: '/settings/pdfs',      label: 'PDFs', adminOnly: true },
  { to: '/settings/team',      label: 'Team' },
  { to: '/settings/import',    label: 'Import',   adminOnly: true },
  {
    // Parent of /settings/developer/{api-keys,webhooks,connectors,integrations}.
    // The legacy URLs (/settings/api-keys etc.) still resolve via
    // <Navigate replace> in App.tsx so docs deep links keep working.
    to: '/settings/developer',
    label: 'Developer',
    adminOnly: true,
    match: '/settings/developer',
  },
  { to: '/settings/billing',   label: 'Billing',  adminOnly: true },
  { to: '/settings/usage',     label: 'Usage' },
];

export function SettingsLayout() {
  const { t } = useTranslation();
  const roleQ = useMyOrgRole();
  const isAdmin = isAdminRole(roleQ.data);
  const canWrite = useCanWriteOrg();
  // Operator = writer but not admin. Viewer = !canWrite.
  // We render admin-only tabs disabled for operators (so they discover
  // they exist) but hide them entirely for viewers (the read-only banner
  // already explains it).
  const isOperator = canWrite && !isAdmin;
  const { pathname } = useLocation();

  return (
    <div className="max-w-4xl">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <SmallCap>{t('settings.layout.eyebrow', 'Workspace settings')}</SmallCap>
          <Display size="md" as="h2" className="mt-1">
            {t('settings.layout.title', 'Settings')}
          </Display>
        </div>
        <div className="flex-1 min-w-[14rem] sm:max-w-sm sm:ml-auto pt-1">
          <SettingsSearch />
        </div>
      </header>

      {/* On phones the page padding is `px-4` (from AppShell) and we
          want the tab underline rule to span the full content edge,
          not just the wrap. `-mx-4 sm:mx-0` lets the scroll container
          bleed edge-to-edge; `px-4 sm:px-0` re-pads the content inside
          so the first tab isn't kissing the left edge. */}
      <nav className="flex items-center gap-1 border-b border-rule mb-8 overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0 sm:flex-wrap">
        {TABS
          // Viewers never see admin-only tabs (read-only banner explains).
          // Operators see them, but rendered disabled with a tooltip below.
          .filter((t) => !t.adminOnly || isAdmin || isOperator)
          .map((t) => {
            const disabledForOperator = !!t.adminOnly && !isAdmin;
            // `match` overrides NavLink's exact-prefix check so the
            // Developer parent tab stays highlighted across every
            // /settings/developer/* sub-route AND across the legacy
            // /settings/{api-keys,webhooks,connectors,integrations}
            // back-compat redirects (those render briefly before the
            // <Navigate> commits).
            const explicitActive = t.match
              ? pathname === t.match || pathname.startsWith(`${t.match}/`)
              : undefined;
            if (disabledForOperator) {
              return (
                <span
                  key={t.to}
                  title={ADMIN_ONLY_TOOLTIP}
                  aria-disabled="true"
                  className="px-3 py-2 text-sm -mb-px border-b-2 whitespace-nowrap text-muted/60 border-transparent cursor-not-allowed"
                >
                  {t.label}
                </span>
              );
            }
            return (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.match ? false : undefined}
                className={({ isActive }) => {
                  const active = explicitActive ?? isActive;
                  return `px-3 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${
                    active
                      ? 'text-ink border-ink'
                      : 'text-muted border-transparent hover:text-ink'
                  }`;
                }}
              >
                {t.label}
              </NavLink>
            );
          })}
      </nav>

      <Outlet />
      <AnchorWatcher />

      <Rule className="mt-12" />
      <p className="mt-4 text-xs text-muted">
        {t('settings.layout.roleSummary', 'Your role in this workspace:')}{' '}
        <strong>{roleQ.data ?? t('settings.layout.roleUnknown', 'unknown')}</strong>.
      </p>
    </div>
  );
}
